import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, HeartHandshake, Check, Trash2, Mail, Reply, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });

export const AdminMessagesPage = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/admin/messages").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e, "Nu am putut încărca mesajele")));
  }, []);

  const markAnswered = async (id) => {
    try {
      await api.patch(`/admin/messages/${id}`, { status: "răspuns" });
      setItems((l) => l.map((m) => (m.id === id ? { ...m, status: "răspuns" } : m)));
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut actualiza"));
    }
  };

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Ștergi mesajul definitiv?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setItems((l) => l.filter((m) => m.id !== id));
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge"));
    }
  };

  return (
    <main data-testid="admin-messages" className="mx-auto max-w-4xl px-6 py-12">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Hai la o cafea</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Mesajele private</h1>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">Răspunzi direct pe e-mail (butonul „Răspunde” îți deschide e-mailul), apoi marchezi mesajul ca răspuns.</p>
      </div>

      {items === null ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68">Se încarcă…</p>
      ) : items.length === 0 ? (
        <div data-testid="messages-empty" className="mt-10 rounded-3xl bg-[var(--tint-pink)] p-12 text-center">
          <HeartHandshake className="mx-auto h-8 w-8 text-[var(--accent)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Niciun mesaj încă.</p>
        </div>
      ) : (
        <ul className="mt-10 space-y-4" data-testid="messages-list">
          {items.map((m) => (
            <li key={m.id} data-testid={`message-${m.id}`} className={`rounded-2xl p-6 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)] ${m.status === "nou" ? "bg-[#2B1B13]" : "bg-[#2B1B13]/60"}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span data-testid={`message-status-${m.id}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${m.status === "nou" ? "bg-[var(--tint-pink)] text-[var(--accent-dark)]" : "bg-[var(--tint-mint)] text-[var(--teal-dark)]"}`}>
                      {m.status}
                    </span>
                    <span className="text-xs text-[#F7F1E8]/68">{fmt(m.created_at)}</span>
                    <span data-testid={`message-mode-${m.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${m.mode === "video" ? "bg-[var(--tint-butter)] text-[var(--sun-dark)]" : "bg-[var(--tint-mint)] text-[var(--teal-dark)]"}`}>
                      {m.mode === "video" ? <><Video className="h-3 w-3" strokeWidth={1.8} /> sesiune video</> : <><Mail className="h-3 w-3" strokeWidth={1.8} /> e-mail</>}
                    </span>
                  </div>
                  {m.mode === "video" && m.availability && (
                    <p className="mt-2 text-xs text-[#F7F1E8]/78">Când i-ar fi bine: <strong>{m.availability}</strong></p>
                  )}
                  <p className="mt-2 font-serif text-xl text-[#F7F1E8]">{m.name}</p>
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 text-sm text-[#F7F1E8]/86 underline decoration-dotted"><Mail className="h-3 w-3" strokeWidth={1.6} />{m.email}</a>
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#F7F1E8]/90">{m.message}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: mesajul tău — La Cafea, Fără Menajamente")}`} data-testid={`message-reply-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white">
                    <Reply className="h-3.5 w-3.5" strokeWidth={1.8} /> Răspunde
                  </a>
                  {m.status === "nou" && (
                    <button type="button" onClick={() => markAnswered(m.id)} data-testid={`message-done-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--milk)_20%,transparent)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--milk)]">
                      <Check className="h-3.5 w-3.5" strokeWidth={2} /> Am răspuns
                    </button>
                  )}
                  <button type="button" onClick={() => remove(m.id)} data-testid={`message-delete-${m.id}`} aria-label="Șterge" className="rounded-full p-2 text-[#F7F1E8]/58 hover:bg-[var(--tint-pink)] hover:text-[var(--accent-dark)]">
                    <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
};

export default AdminMessagesPage;
